import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getMoviesByCategory, type Movie } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug || 'dongzuo');
  const [activeSlug, setActiveSlug] = useState(slug || 'dongzuo');
  const [movies, setMovies] = useState<Movie[]>([]);

  useEffect(() => {
    setActiveSlug(slug || 'dongzuo');
    setMovies(getMoviesByCategory(slug || 'dongzuo'));
  }, [slug]);

  const category = getCategoryBySlug(activeSlug);

  return (
    <>
      {/* Schema.org - BreadcrumbList */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.aiyaaiya.com/' },
            { '@type': 'ListItem', position: 2, name: category.name, item: `https://www.aiyaaiya.com/category/${activeSlug}` },
          ],
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Page Header */}
          <section className="pt-24 pb-8 md:pt-28 md:pb-10 bg-gradient-to-b from-background-100 to-background-50">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <span>/</span>
                <span className="text-foreground-600">{category.name}</span>
              </nav>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-accent-100/60">
                  <i className={`${category.icon} text-accent-600 text-xl md:text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">{category.name}</h1>
                  <p className="text-sm text-foreground-500 mt-0.5">共 {movies.length} 部影片</p>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-6">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Movie Grid */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-14">
            {movies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {movies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3">
                      <img
                        src={movie.image}
                        alt={movie.title}
                        title={`${movie.title} - 爱丫爱丫影院`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
                        {movie.isNew && (
                          <span className="px-2 py-0.5 bg-primary-500 text-white text-[10px] font-semibold rounded-md whitespace-nowrap">
                            NEW
                          </span>
                        )}
                        {movie.isHot && (
                          <span className="px-2 py-0.5 bg-accent-500 text-foreground-900 text-[10px] font-semibold rounded-md whitespace-nowrap">
                            热播
                          </span>
                        )}
                      </div>
                      <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/60 backdrop-blur-sm text-accent-400 text-xs font-semibold rounded-md">
                        <i className="ri-star-fill text-[10px] mr-0.5"></i>
                        {movie.rating}
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                        <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <i className="ri-play-fill text-primary-500 text-xl md:text-2xl ml-0.5"></i>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {movie.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-foreground-400">{movie.year}</span>
                      <span className="text-[10px] text-foreground-300">·</span>
                      <span className="text-xs text-foreground-400">{movie.duration}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-movie-2-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无影片，请查看其他分类</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}